import React from 'react';
import {
  Alert,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  DEFAULT_HAZARD_CLASS_KEY,
  DEFAULT_SAFETY_REPORT_TYPE_MODE,
  type HazardClassKey,
  type SafetyReportTypeMode,
  V1_HAZARD_CLASS_OPTIONS,
} from './hazardClassReportOptions';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';

export const REPORT_TYPE_FIELD_LABEL = 'Choose report view';

export const REPORT_TYPE_PATTERN_LABEL = displaySafetyManagerLabel('report_mode_pattern');

export const REPORT_TYPE_HAZARD_CLASS_LABEL = 'Risk category report';

export const HAZARD_CLASS_FIELD_LABEL = 'Risk category';

export const REPORT_TYPE_PATTERN_HELPER =
  'Summarizes one recurring risk pattern with representative evidence.';

export const REPORT_TYPE_HAZARD_CLASS_HELPER =
  'Summarizes all recurring risks for one risk category in the site and time window. Does not claim accident prevention or risk elimination.';

export const HAZARD_CLASS_FIELD_HELPER =
  'Select which precursor hazard family to include. Patterns are grouped by cloud policy taxonomy.';

type Props = {
  reportTypeMode: SafetyReportTypeMode;
  onReportTypeModeChange: (mode: SafetyReportTypeMode) => void;
  hazardClassKey: HazardClassKey;
  onHazardClassKeyChange: (key: HazardClassKey) => void;
  disabled?: boolean;
};

export default function SafetyReportTypeControls({
  reportTypeMode,
  onReportTypeModeChange,
  hazardClassKey,
  onHazardClassKeyChange,
  disabled = false,
}: Props) {
  const selectedOption =
    V1_HAZARD_CLASS_OPTIONS.find((option) => option.key === hazardClassKey)
    || V1_HAZARD_CLASS_OPTIONS[0];

  return (
    <Stack spacing={1.25} data-testid="safety-report-type-controls">
      <FormControl component="fieldset" disabled={disabled}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          {REPORT_TYPE_FIELD_LABEL}
        </Typography>
        <RadioGroup
          row
          value={reportTypeMode}
          onChange={(event) =>
            onReportTypeModeChange(
              event.target.value === 'hazard_class' ? 'hazard_class' : DEFAULT_SAFETY_REPORT_TYPE_MODE,
            )}
          sx={{ mt: 0.5 }}
        >
          <FormControlLabel
            value="pattern"
            control={<Radio size="small" inputProps={{ 'aria-label': REPORT_TYPE_PATTERN_LABEL } as object} />}
            label={REPORT_TYPE_PATTERN_LABEL}
          />
          <FormControlLabel
            value="hazard_class"
            control={<Radio size="small" inputProps={{ 'aria-label': REPORT_TYPE_HAZARD_CLASS_LABEL } as object} />}
            label={REPORT_TYPE_HAZARD_CLASS_LABEL}
          />
        </RadioGroup>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
          {reportTypeMode === 'hazard_class' ? REPORT_TYPE_HAZARD_CLASS_HELPER : REPORT_TYPE_PATTERN_HELPER}
        </Typography>
      </FormControl>

      {reportTypeMode === 'hazard_class' ? (
        <Stack spacing={1}>
          <FormControl size="small" fullWidth disabled={disabled}>
            <InputLabel id="hazard-class-select-label">{HAZARD_CLASS_FIELD_LABEL}</InputLabel>
            <Select
              labelId="hazard-class-select-label"
              label={HAZARD_CLASS_FIELD_LABEL}
              value={hazardClassKey || DEFAULT_HAZARD_CLASS_KEY}
              onChange={(event) =>
                onHazardClassKeyChange(String(event.target.value) as HazardClassKey)}
              data-testid="hazard-class-select"
            >
              {V1_HAZARD_CLASS_OPTIONS.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.displayName}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', lineHeight: 1.45 }}>
              {HAZARD_CLASS_FIELD_HELPER}
            </Typography>
          </FormControl>
          <Alert severity="info" sx={{ py: 0.35 }}>
            {selectedOption.claimBoundary}
          </Alert>
        </Stack>
      ) : null}
    </Stack>
  );
}
